import type {
  BuilderCondition,
  BuilderRule,
  BuilderState,
  PolicyRuleDecision
} from "../components/policyBuilder/builderTypes.js";
import { getFieldDef, opLabelInline } from "../components/policyBuilder/fieldCatalog.js";

export const policyModeLabel: Record<BuilderState["mode"], string> = {
  allow: "Allow all",
  deny: "Deny all",
  require_approval: "Always approve",
  conditional: "Custom rules"
};

export const policyModeDescription: Record<BuilderState["mode"], string> = {
  allow: "Every request is permitted immediately",
  deny: "Every request is blocked",
  require_approval: "Every request goes to the approver inbox",
  conditional: "Evaluate ordered rules, then default"
};

export const ruleDecisionLabel: Record<PolicyRuleDecision, string> = {
  allow: "Allow",
  deny: "Deny",
  require_approval: "Require approval",
  require_modified_parameters: "Modify parameters"
};

export const ruleDecisionBadgeVariant = (
  decision: PolicyRuleDecision
): "success" | "destructive" | "warning" | "secondary" => {
  if (decision === "allow") return "success";
  if (decision === "deny") return "destructive";
  if (decision === "require_approval") return "warning";
  return "secondary";
};

const formatConditionValue = (cond: BuilderCondition): string => {
  if (Array.isArray(cond.value)) {
    return cond.value.join(", ");
  }
  return String(cond.value);
};

export const formatConditionChip = (cond: BuilderCondition): string => {
  const field = getFieldDef(cond.field);
  const op = opLabelInline(cond.op, field.type);
  const val = formatConditionValue(cond);
  if (cond.op === "in" || cond.op === "not_in") {
    return `${field.label} ${op} ${val}`;
  }
  return `${field.label} ${op} ${val}`;
};

export const ruleTitleFromConditions = (rule: BuilderRule): string => {
  if (rule.conditions.length === 0) {
    return "New rule";
  }
  if (rule.conditions.length === 1) {
    const c = rule.conditions[0]!;
    if (c.field === "amount" && c.op === "gte") {
      return `When amount ≥ ${formatConditionValue(c)}`;
    }
    if (c.field === "amount" && c.op === "lte") {
      return `When amount ≤ ${formatConditionValue(c)}`;
    }
    if (c.field === "amount" && c.op === "gt") {
      return `When amount > ${formatConditionValue(c)}`;
    }
    return `When ${formatConditionChip(c)}`;
  }
  return `When ${rule.conditions.length} conditions match`;
};

export const defaultDecisionLabel = (decision: BuilderState["defaultDecision"]): string => {
  if (decision === "allow") return "Allow";
  if (decision === "deny") return "Deny";
  return "Require approval";
};
